import { Box } from "@mui/material";
import { createTheme, ThemeProvider, useTheme } from "@mui/material/styles";
import { type ReactNode, useMemo } from "react";
import { useDepartmentFlags } from "../hooks/useDepartmentFlags";
import { DEMO_FONT_FAMILY } from "./tokens";

/**
 * Forces the whole hospital demo (inhouse2 + Hospital, `isHospitalView`) to render in DM Sans by
 * overriding only the theme's font families. Because MUI reads fonts from the nearest React-tree
 * ThemeProvider, this reaches every MUI component, dialog, AND portal in one place — no per-page
 * font overrides. Every other department keeps the user's theme untouched (this is a pass-through).
 */
export const HospitalDemoThemeProvider = ({ children }: { children: ReactNode }) => {
	const { isHospitalView } = useDepartmentFlags();
	const baseTheme = useTheme();

	const demoTheme = useMemo(() => {
		if (!isHospitalView) return baseTheme;
		// Set fontFamily on the root AND every variant explicitly — MUI bakes each variant's font at
		// build time and body1/body2/subtitle don't inherit the root, so they'd otherwise stay Inter.
		// All other theme values (colors, spacing, component overrides) are kept from the base theme.
		return createTheme(baseTheme, {
			typography: {
				fontFamily: DEMO_FONT_FAMILY,
				h1: { fontFamily: DEMO_FONT_FAMILY },
				h2: { fontFamily: DEMO_FONT_FAMILY },
				h3: { fontFamily: DEMO_FONT_FAMILY },
				h4: { fontFamily: DEMO_FONT_FAMILY },
				h5: { fontFamily: DEMO_FONT_FAMILY },
				h6: { fontFamily: DEMO_FONT_FAMILY },
				subtitle1: { fontFamily: DEMO_FONT_FAMILY },
				subtitle2: { fontFamily: DEMO_FONT_FAMILY },
				body1: { fontFamily: DEMO_FONT_FAMILY },
				body2: { fontFamily: DEMO_FONT_FAMILY },
				button: { fontFamily: DEMO_FONT_FAMILY },
				caption: { fontFamily: DEMO_FONT_FAMILY },
				overline: { fontFamily: DEMO_FONT_FAMILY },
			},
			components: {
				// A few MUI inputs bake their own font at build time and ignore the typography variants
				// above, so force DM Sans on their roots too (form labels, inputs, chips, menu items).
				MuiFormLabel: { styleOverrides: { root: { "&.MuiFormLabel-root": { fontFamily: DEMO_FONT_FAMILY } } } },
				MuiInputLabel: { styleOverrides: { root: { "&.MuiInputLabel-root": { fontFamily: DEMO_FONT_FAMILY } } } },
				MuiInputBase: { styleOverrides: { root: { fontFamily: DEMO_FONT_FAMILY } } },
				MuiChip: { styleOverrides: { root: { fontFamily: DEMO_FONT_FAMILY } } },
				MuiMenuItem: { styleOverrides: { root: { fontFamily: DEMO_FONT_FAMILY } } },
				MuiTab: { styleOverrides: { root: { fontFamily: DEMO_FONT_FAMILY } } },
			},
		});
	}, [isHospitalView, baseTheme]);

	// Non-demo departments render the exact same children — no extra provider, no font change.
	if (!isHospitalView) return <>{children}</>;

	// Wrap in a Box that sets fontFamily so raw (non-MUI) <div>/<span> text inside the demo also
	// inherits DM Sans via the CSS cascade; the ThemeProvider handles all MUI components + portals.
	return (
		<ThemeProvider theme={demoTheme}>
			<Box sx={{ fontFamily: DEMO_FONT_FAMILY, height: "100%", width: "100%" }}>{children}</Box>
		</ThemeProvider>
	);
};
